import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import { Category, DataPoint } from "fileParsing/types";
import { Theme, useTheme } from "@mui/material/styles";
import { useDatapointsFromExportId } from "../hooks/useData";
import { Box } from "@mui/system";

export type KeyValuePair = { key: string; value: number; color?: string };

/**
 * Computes the linear scales for the x and y dimension for the given data and chart sizes
 */
function getScales(data: KeyValuePair[], chartWidth: number, chartHeight: number) {
  const { keys, valueRange } = computeDataRange(data);

  return {
    xScale: d3.scaleBand().domain(keys).range([0, chartWidth]).padding(0.2),
    yScale: d3.scaleLinear().domain(valueRange).range([chartHeight, labelOffset]).nice(),
  };
}

/**
 * Returns the data ranges (min/max) for the x and y dimension
 */
function computeDataRange(data: KeyValuePair[]): {
  keys: string[];
  valueRange: number[];
} {
  if (data.length === 0) {
    throw new Error("Input data array is empty");
  }
  const keySet = new Set<string>();
  let maxValue = data[0].value;

  for (const point of data) {
    keySet.add(point.key);
    if (point.value > maxValue) {
      maxValue = point.value;
    }
  }
  return {
    keys: Array.from(keySet),
    valueRange: [0, maxValue], // Always set 0 as min value
  };
}

type ComponentProps = {
  exportIds?: number[];
  hideAxis?: boolean;
};

const marginAxis = { top: 10, right: 20, bottom: 40, left: 60 };
const marginNoAxis = { top: 0, right: 0, bottom: 0, left: 0 };
const defaultColor = "#5FAF77";
const labelOffset = 16;

const getBarChartData = (dataset: DataPoint[], theme: Theme) => {
  return Object.values(Category).map((category) => {
    const items = dataset.reduce((acc1, val1) => acc1 + (val1.category === category ? 1 : 0), 0);

    return {
      key: category,
      color: theme.palette.categories[category].main,
      value: items,
    };
  });
};

function BarChart(props: ComponentProps & SizeProps) {
  const theme = useTheme();

  /* The useRef Hook creates a variable that "holds on" to a value across rendering
     passes. In this case it will hold our component's SVG DOM element. It's
     initialized null and React will assign it later (see the return statement) */
  const d3Container = useRef<SVGSVGElement>(null);

  const margin = props.hideAxis ? marginNoAxis : marginAxis;

  const fullData = useDatapointsFromExportId(props.exportIds);
  const data = getBarChartData(fullData, theme);

  const d3ScaleX = useRef<d3.ScaleBand<string> | null>(null);
  const { chartWidth, chartHeight } = getChartSize();
  const { xScale, yScale } = getScales(data, chartWidth, chartHeight);

  /**
   * Returns the width, height of the actual chart drawing area (total SVG size minus margins)
   */
  function getChartSize() {
    return {
      chartWidth: props.size.width - margin.left - margin.right,
      chartHeight: props.size.height - margin.top - margin.bottom,
    };
  }

  function getTransition() {
    return d3.transition().duration(150).ease(d3.easeLinear);
  }

  /**
   * This useEffect block is run only once after the component mounts. We will do the initial D3 setup here.
   */
  useEffect(() => {
    if (!d3Container.current) return;

    const svg = d3.select(d3Container.current);
    if (svg.selectAll(".container").size() > 0) return;

    // Add the chart drawing area (group with margin translations)
    const svgChart = svg
      .append("g")
      .attr("class", "container")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Create group that will later hold the bars' <rect> elements
    svgChart.append("g").attr("class", "bars");
    svgChart.append("g").attr("class", "labels");

    // Add X axis
    if (!props.hideAxis)
      svgChart
        .append("g")
        .attr("class", "xAxis")
        .attr("transform", `translate(0, ${chartHeight})`)
        .call(d3.axisBottom(xScale))
        .selectAll("text")
        .attr("transform", "translate(0,2) scale(0.8)")
        .style("text-anchor", "center");
    // Enable this, if you want 45° rotated axis markers
    // .selectAll("text")
    // .attr("transform", "translate(-10,0)rotate(-45)")
    // .style("text-anchor", "end");

    d3ScaleX.current = xScale;

    // Add Y axis
    if (!props.hideAxis)
      svgChart
        .append("g")
        .attr("class", "yAxis")
        .call(d3.axisLeft(yScale))
        .selectAll("text")
        .attr("transform", "translate(-4,0) scale(0.8)")
        .style("text-anchor", "end");
  }, [fullData.length]);

  /**
   * This useEffect block is run every time the data or component sizes change. We will do every chart updates here.
   */
  useEffect(() => {
    if (!d3Container.current) return;

    const { chartWidth, chartHeight } = getChartSize();
    const { xScale, yScale } = getScales(data, chartWidth, chartHeight);

    const svg = d3.select(d3Container.current);

    // Update X Axis (Note: instead of .select we could also store a ref for this object - likely more efficient)
    if (!props.hideAxis)
      svg
        .select<SVGGElement>(".xAxis")
        .attr("transform", "translate(0," + chartHeight + ")")
        .attr("font-size", `min(calc((${chartWidth}px / ${data.length * 3.4})), 18px)`)
        .call(d3.axisBottom(xScale));

    // Update Y Axis
    if (!props.hideAxis)
      svg.select<SVGGElement>(".yAxis").attr("font-size", "min(3.5vw, 18px)").call(d3.axisLeft(yScale));
    // Enable this, if you want 45° rotated axis markers
    // .selectAll("text")
    // .attr("transform", "translate(-10,0)rotate(-45)")
    // .style("text-anchor", "end");

    d3ScaleX.current = xScale;

    // Update data point rects (Note: instead of .select we could also store a ref for this object - likely more efficient)
    const barsGroup = svg.select<SVGGElement>(".bars");
    const bars = barsGroup.selectAll("rect").data(data);

    const labelsGroup = svg.select<SVGGElement>(".labels");
    const labels = labelsGroup.selectAll("text").data(data);

    // Enter new elements
    bars
      .enter()
      .append<SVGRectElement>("rect")
      .attr("x", (d) => xScale(d.key) || null)
      .attr("y", (d) => yScale(d.value))
      .attr("height", (d) => chartHeight - yScale(d.value))
      .attr("width", xScale.bandwidth())
      .style("fill", (d) => d.color ?? defaultColor);

    labels
      .enter()
      .append<SVGRectElement>("text")
      .attr("x", (d) => (xScale(d.key) ?? 0) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.value) - labelOffset)
      .attr("dy", ".75em")
      .attr("class", "label")
      .style("fill", theme.palette.text.primary)
      .style("font-size", "0.9em")
      .style("text-anchor", "middle")
      .text((d) => d.value);

    // Update existing  elements
    bars
      .transition(getTransition())
      .attr("x", (d) => xScale(d.key) || null)
      .attr("y", (d) => yScale(d.value))
      .attr("height", (d) => chartHeight - yScale(d.value))
      .attr("width", xScale.bandwidth());

    labels
      // .selectAll(".text")
      .transition(getTransition())
      .attr("x", (d) => (xScale(d.key) ?? 0) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.value) - labelOffset)
      .text((d) => d.value);

    // Remove old elements
    bars.exit().remove();
    labels.exit().remove();
  }, [data, props.size]);

  if (data.reduce((prev, curr) => prev + curr.value, 0) === 0) return <></>;

  return (
    <Box>
      <svg
        width={props.size.width}
        height={props.size.height}
        ref={d3Container}
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        viewBox={`0 0 ${props.size.width} ${props.size.height}`}
      />
    </Box>
  );
}

export default ResizeComponent(BarChart);
